import BaseService from "./BaseService";
import {
    KboFlowRunDebug,
    KboFlowRunsPage,
} from "components/Admin/Organizations/OrganizationDetail/KboFlowDebug/KboFlowRunDebug";

/** A run offered by the Flow Step Debug run picker. */
export interface KboFlowStepDebugRun {
    id: string;
    subjectUserId: string;
    runId?: string | null;
    createdOn: string;
    startedAtUtc?: string | null;
    completedAtUtc?: string | null;
    /** Headline research question the run worked on; null when it generated none. */
    question?: string | null;
    questionCount: number;
    /** Phase records the run executed. */
    stepCount: number;
}

export interface KboFlowStepDebugRunsResult {
    runs: KboFlowStepDebugRun[];
    /** Rows the server read and parsed to build this result. */
    scannedCount: number;
    /** True when the server's scan window filled, so older matching runs may exist. */
    truncated: boolean;
}

export interface KboFlowStepDebugRunsQuery {
    /** Kbo.Flow phase names the run must have completed; empty matches any run. */
    phases: string[];
    /** How far back to look; 0 means any time. */
    days: number;
    /** Restrict to runs requested for one user; omit for any user. */
    subjectUserId?: string;
    /** Case-insensitive substring match against the run's headline question. */
    search?: string;
    limit?: number;
}

/** One skill's edited instructions, in force for a single step-debug execution. */
export interface KboFlowStepDebugSkillOverride {
    name: string;
    skillText: string;
}

export interface KboFlowStepDebugExecuteRequest {
    sourceRunId: string;
    /** Step id from the flow-step catalog, e.g. "question-generation". */
    stepName: string;
    /** Exploration node to analyze; used by analyze-one-node, defaults to the first node. */
    nodeId?: string;
    /** Exploration depth for the depth-scoped steps. Defaults to 0. */
    depth?: number;
    skillOverrides: KboFlowStepDebugSkillOverride[];
}

export interface KboFlowStepDebugMetrics {
    elapsedMs: number;
    invocations: number;
    inputTokens: number;
    outputTokens: number;
    cachedInputTokens: number;
    model?: string | null;
}

/** What the step produced. Stored nowhere — this is the only copy. */
export interface KboFlowStepDebugExecuteResult {
    success: boolean;
    /** Why the step failed. A failed step still returns 200. */
    error?: string | null;
    /** The step's output as JSON text. */
    output?: string | null;
    /**
     * What the source run produced for this same step, same shape as {@link output} so the two can
     * be lined up path by path. Null when the source run has nothing for the step.
     */
    originalOutput?: string | null;
    /** Schema version of the source run's stored output, for labelling the comparison. */
    sourceRunVersion: number;
    metrics: KboFlowStepDebugMetrics;
}

export interface KboFlowStepDebugJudgeRequest {
    stepName: string;
    stepLabel: string;
    /** Skill whose edit is being judged; selects which judge prompt runs. */
    skillName: string;
    /**
     * Both sides of the edit, not a diff — the server decides whether the change alters meaning at
     * all, so a whitespace-only edit is never presented to the judge as a content change.
     */
    originalSkillText: string;
    editedSkillText: string;
    originalOutput?: string | null;
    producedOutput?: string | null;
}

/** One skill's judgement. Returned inline and stored nowhere. */
export interface KboFlowStepDebugJudgement {
    skillName?: string | null;
    success: boolean;
    /** Why the judge could not run. Null on success. */
    error?: string | null;
    /** "improved", "regressed", or "unchanged". */
    verdict?: string | null;
    summary?: string | null;
    modelName?: string | null;
}

/** Parameters for scheduling a KboFlow run (tenant + user are resolved server-side). */
export interface StartKboFlowWorkflowRequest {
    workflowId: string;
    startDateUtc: string;
    endDateUtc: string;
    targetQuestionCount: number;
    maxQuestionsToPursue: number;
    /** Override the run's subject user; omit/undefined uses the current user. */
    subjectUserId?: string;
    ignoreHistory: boolean;
    /** Prior KboFlow run id whose phase outputs should seed the new run. */
    replaySourceRunId?: string;
    /** First phase to execute when replaying, e.g. "ReportAssembly". */
    rerunFromPhase?: string;
}

export interface StartKboFlowWorkflowResponse {
    correlationId: string;
}

export interface SendReportEmailResponse {
    /**
     * Email addresses the report was sent to. Defaults to the current user's own
     * address when no recipients are supplied.
     */
    sentTo: string[];
}

export interface KboFlowAnalysisMaterialItem {
    conversationId: string;
    displayOrder: number;
    text: string;
    wasAnalyzed: boolean;
    matchesResult: boolean;
}

export interface KboFlowAnalysisMaterial {
    materialSource?: string;
    reasoningModelName?: string;
    analyzedConversationCount: number;
    matchingConversationCount: number;
    totalConversationCount: number;
    items: KboFlowAnalysisMaterialItem[];
}

export interface RerunKboFlowWorkflowRequest {
    /** First phase to execute when replaying, e.g. "ReportAssembly". */
    rerunFromPhase?: string;
    /** Optional model override; omit/undefined keeps the source run model/default. */
    modelId?: string;
}

/**
 * Reads Kbo.Flow agent runs persisted to the tenant-scoped KboFlowRuns table and schedules
 * new runs. Org scoping is handled automatically by the Acx-OrganizationId request header.
 * Ultra-only on the backend (see KboFlowDebugController).
 */
export class KboFlowDebugService extends BaseService {
    protected urlBase = "api/KboFlowDebug";

    /** Fetches one page of lightweight run summaries (no RunOutput). */
    public async getRuns(
        page: number,
        pageSize: number,
        abortSignal?: AbortSignal,
    ): Promise<KboFlowRunsPage> {
        return this.get<KboFlowRunsPage>(
            new URLSearchParams({
                page: String(page),
                pageSize: String(pageSize),
            }),
            "Runs",
            abortSignal,
        );
    }

    /**
     * Fetches completed runs that executed a given flow step, for the Flow Step Debug run
     * picker. The step and question-text filters are applied server-side against each run's
     * stored output, so this returns a bounded, already-filtered list rather than a page.
     */
    public async getStepDebugRuns(
        query: KboFlowStepDebugRunsQuery,
        abortSignal?: AbortSignal,
    ): Promise<KboFlowStepDebugRunsResult> {
        const params = new URLSearchParams({
            phase: query.phases.join(","),
            days: String(query.days),
        });
        if (query.subjectUserId) params.set("subjectUserId", query.subjectUserId);
        if (query.search) params.set("search", query.search);
        if (query.limit) params.set("limit", String(query.limit));

        return this.get<KboFlowStepDebugRunsResult>(
            params,
            "StepDebugRuns",
            abortSignal,
        );
    }

    /**
     * Runs one flow step against a completed run with the given skill edits and returns what it
     * produced. The call is held open for the length of the step — minutes, for a report — and
     * nothing about the execution is persisted on either side.
     */
    public async runStepDebug(
        request: KboFlowStepDebugExecuteRequest,
        abortSignal?: AbortSignal,
    ): Promise<KboFlowStepDebugExecuteResult> {
        return this.post<KboFlowStepDebugExecuteResult>(
            JSON.stringify(request),
            new URLSearchParams(),
            "StepDebugRun",
            { returnJson: true },
            undefined,
            abortSignal,
        );
    }

    /**
     * Judges whether one skill's edit improved the step's output. One call per edited skill, since
     * each skill has its own judge prompt.
     */
    public async judgeStepDebug(
        request: KboFlowStepDebugJudgeRequest,
        abortSignal?: AbortSignal,
    ): Promise<KboFlowStepDebugJudgement> {
        return this.post<KboFlowStepDebugJudgement>(
            JSON.stringify(request),
            new URLSearchParams(),
            "StepDebugJudge",
            { returnJson: true },
            // BaseService.post is debounced on url + params, so judging several skills at once would
            // collapse into one call and leave the other promises pending forever. The skill name makes
            // each concurrent judgement its own debounce bucket.
            `StepDebugJudge-${request.skillName}`,
            abortSignal,
        );
    }

    /** Fetches a single run's full parsed RunOutput (on expand). */
    public async getRun(
        runId: string,
        abortSignal?: AbortSignal,
    ): Promise<KboFlowRunDebug> {
        return this.get<KboFlowRunDebug>(
            new URLSearchParams(),
            `Runs/${runId}`,
            abortSignal,
        );
    }

    public async getAnalysisMaterialPreview(
        runId: string,
        nodeId: string,
        resultId: string,
        abortSignal?: AbortSignal,
    ): Promise<KboFlowAnalysisMaterial> {
        return this.get<KboFlowAnalysisMaterial>(
            new URLSearchParams({ nodeId, resultId }),
            `Runs/${runId}/AnalysisMaterial/Preview`,
            abortSignal,
        );
    }

    public async getAnalysisMaterial(
        runId: string,
        nodeId: string,
        resultId: string,
        abortSignal?: AbortSignal,
    ): Promise<KboFlowAnalysisMaterial> {
        return this.get<KboFlowAnalysisMaterial>(
            new URLSearchParams({ nodeId, resultId }),
            `Runs/${runId}/AnalysisMaterial`,
            abortSignal,
        );
    }

    public async startWorkflow(
        request: StartKboFlowWorkflowRequest,
        abortSignal?: AbortSignal,
    ): Promise<StartKboFlowWorkflowResponse> {
        return this.post<StartKboFlowWorkflowResponse>(
            JSON.stringify(request),
            new URLSearchParams(),
            "StartWorkflow",
            { returnJson: true },
            undefined,
            abortSignal,
        );
    }

    public async rerunWorkflow(
        sourceRunId: string,
        request: RerunKboFlowWorkflowRequest,
        abortSignal?: AbortSignal,
    ): Promise<StartKboFlowWorkflowResponse> {
        return this.post<StartKboFlowWorkflowResponse>(
            JSON.stringify(request),
            new URLSearchParams(),
            `Runs/${sourceRunId}/Rerun`,
            { returnJson: true },
            undefined,
            abortSignal,
        );
    }

    /**
     * Emails the run's generated report to the supplied recipients. When
     * {@link recipients} is empty, the server falls back to the current user's
     * own address. {@link reportId} identifies the persisted report selected in the
     * run output.
     */
    public async sendReportEmail(
        runId: string,
        recipients: string[] = [],
        reportId: string,
        abortSignal?: AbortSignal,
    ): Promise<SendReportEmailResponse> {
        return this.post<SendReportEmailResponse>(
            JSON.stringify({ recipients, reportId }),
            new URLSearchParams(),
            `Runs/${runId}/SendReportEmail`,
            { returnJson: true },
            undefined,
            abortSignal,
        );
    }
}
